import React, { useMemo, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useToast } from 'src/context/ToastContext';

import {
	useAddStatementPagesMutation,
	useConfirmLineItemsMutation,
	useRerunStatementMutation,
	useSetDiscountRateMutation,
} from '../api';
import { useFeature } from '../hooks';
import { useAnalysisData } from '../hooks/useAnalysisData';
import { useIsoProfitAccess } from '../hooks/useIsoProfitAccess';
import { useOnboarding } from '../hooks/useOnboarding';
import {
	discountLines,
	projectDiscountRate,
} from '../utils/discountRateProjection';
import { formatRatePlanType } from '../utils/utils';

import AddPagesModal from './analysis/AddPagesModal';
import AnalysisHeader from './analysis/AnalysisHeader';
import AnalysisSideCards, {
	checklistItems,
} from './analysis/AnalysisSideCards';
import AnalysisStep from './analysis/AnalysisStep';
import BounceReplyDraft, { needsReply } from './analysis/BounceReplyDraft';
import DocumentCheckBanner, {
	blocksQuote,
	isNotAStatement,
	type UploadAction,
} from './analysis/DocumentCheckBanner';
import DiscountRateDock, {
	quoteDiscountRate,
	type DiscountRateSlider,
} from './analysis/DiscountRateDock';
import DocumentViewerPanel from './analysis/DocumentViewerPanel';
import {
	isPartlyReadable,
	lineReviewFrom,
	LineReviewProvider,
	PartlyReadableBanner,
	PersonReviewBanner,
	quoteLockedMessage,
} from './analysis/LineReview';
import ProcessingStep from './analysis/ProcessingStep';
import StatementStateView from './analysis/StatementStateView';
import { Button, Modal, ModalBody, ModalFooter } from './common';

import type { ComparisonStatement } from '../types';

interface Document {
	id: string;
	name: string;
	type: string;
	date: string;
	size: string;
	status: string;
	processingMonth: string;
	pdfUrl?: string;
	results?: any;
	uploaded_by?: { name: string; email: string };
}

interface AnalysisViewProps {
	document: Document;
	onBack: () => void;
	comparisonData?: ComparisonStatement | null;
	onRefresh?: () => void;
	onboardingContext?: boolean;
	onOnboardingComplete?: () => void;
}

const AnalysisView: React.FC<AnalysisViewProps> = ({
	document,
	onBack,
	onRefresh,
	comparisonData,
	onboardingContext = false,
	onOnboardingComplete,
}) => {
	const navigate = useNavigate();
	const { nextStep } = useOnboarding();
	const [isDocumentVisible, setIsDocumentVisible] = useState(false);
	const [isRerunModalOpen, setIsRerunModalOpen] = useState(false);
	const [isCustomizeModalOpen, setIsCustomizeModalOpen] = useState(false);
	// ROC-62: "Keep my selection" dismisses the type-mismatch banner for this
	// statement only, so navigating to another statement re-evaluates it.
	const [dismissedMismatchForId, setDismissedMismatchForId] = useState<
		string | null
	>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const { showToast } = useToast();
	const [rerunStatement, { isLoading: isRerunning }] =
		useRerunStatementMutation();
	const [addStatementPages, { isLoading: isAddingPages }] =
		useAddStatementPagesMutation();
	const [setDiscountRate] = useSetDiscountRateMutation();
	const [uploadRequest, setUploadRequest] = useState<UploadAction | null>(null);

	// View mode state for switching between Default/User Edits/Customized Quote views
	const [viewMode, setViewMode] = useState<
		'default' | 'user_edits' | 'customized_quote'
	>('default');

	// Use custom hook for all analysis data management
	const {
		localDocumentStatus,
		isLogsCollapsed,
		setIsLogsCollapsed,
		logsData,
		refetchLogs,
		refetch,
		refreshedDocument,
		isFetching,
		isLoading,
		analysisData,
		monthlySavingsData,
		calculatedTotals, // Backend-calculated totals (statement-type-aware)
		revenueAnalysis, // ISO Profit - reactive to line item edits
		enhancedDocument,
		showProgress,
		customRates,
		analysisType,
		actionType,
		// Statement-type mismatch (ROC-62)
		selectedRatePlanType,
		detectedRatePlanType,
		hasTypeMismatch,
		// Line item editing support
		handleLineItemUpdated,
		enableEditing,
		// View mode helpers
		hasUserEdits,
		hasCustomizedQuote,
		lineItemsData,
	} = useAnalysisData({
		document,
		comparisonData,
		enableLineItemsFromDB: true,
		viewMode,
	});

	const toggleDocumentVisibility = () => {
		setIsDocumentVisible(!isDocumentVisible);
	};

	const handleCustomizeFeeStructure = (feeData: any) => {
		// Here you can implement the logic to:
		// 1. Update analysis calculations with custom fee structure
		// 2. Refresh the analysis data
		// 3. Show updated savings/costs
		// For now, just log the data
	};

	// Refresh everything after a Customize-Quote recalculation. Platform-engine
	// recalculation is synchronous (no processing -> completed transition to
	// drive the usual logs-poll refetch), so we explicitly refetch the logs,
	// the detailed document, and the line items table to surface the new
	// proposed totals / IC+ values without a manual reload.
	const handlePostRecalculation = () => {
		refetchLogs();
		if (refetch) {
			refetch();
		}
		handleLineItemUpdated();
	};

	const handleOpenRerunModal = () => {
		if (localDocumentStatus === 'processing') {
			return;
		}
		setIsRerunModalOpen(true);
	};

	const handleCloseRerunModal = () => {
		setIsRerunModalOpen(false);
	};

	const handleConfirmRerun = async () => {
		try {
			await rerunStatement({
				fileId: document.id,
				skipCache: true,
				invalidateCache: true,
				deleteResults: false,
			}).unwrap();

			showToast(
				'Rerun Started',
				'We’re reprocessing this statement from scratch. This may take a few minutes.',
				'success',
			);

			setIsLogsCollapsed(false);
			setIsRerunModalOpen(false);
			refetchLogs();
			if (refetch) {
				refetch();
			}
		} catch (error: any) {
			const message =
				error?.data?.message ||
				error?.message ||
				'Failed to start rerun. Please try again.';
			showToast(
				error?.status === 402 ? 'No reports left' : 'Rerun Failed',
				message,
				'error',
			);
		}
	};

	// ROC-90: the missing page (append) or the right statement (replace), then rerun.
	const handleAddPages = async (files: File[]) => {
		if (!uploadRequest) return;
		try {
			await addStatementPages({
				fileId: document.id,
				files,
				mode: uploadRequest.mode,
			}).unwrap();
			showToast(
				'Analyzing again',
				'We added your file and are analyzing the statement again.',
				'success',
			);
			setUploadRequest(null);
			setIsLogsCollapsed(false);
			refetchLogs();
			if (refetch) {
				refetch();
			}
		} catch (error: any) {
			const message =
				error?.data?.message ||
				error?.message ||
				'Could not add the file. Please try again.';
			showToast(
				error?.status === 402 ? 'No reports left' : 'Upload failed',
				message,
				'error',
			);
		}
	};

	// ROC-62: rerun as the classifier-detected type, loading that type's
	// default rate configuration ("Process as {detected}" banner action).
	const handleRecalculateAsDetected = async () => {
		if (!detectedRatePlanType || localDocumentStatus === 'processing') {
			return;
		}
		try {
			await rerunStatement({
				fileId: document.id,
				skipCache: true,
				invalidateCache: true,
				deleteResults: false,
				targetRatePlanType: detectedRatePlanType,
			}).unwrap();

			showToast(
				'Recalculation Started',
				`We're reanalyzing this statement as ${formatRatePlanType(detectedRatePlanType)} with its default rate configuration.`,
				'success',
			);

			setIsLogsCollapsed(false);
			refetchLogs();
			if (refetch) {
				refetch();
			}
		} catch (error: any) {
			const message =
				error?.data?.message ||
				error?.message ||
				'Failed to start recalculation. Please try again.';
			showToast('Recalculation Failed', message, 'error');
		}
	};

	// ROC-116: a flagged check from this run shows before the rest of the run ends.
	const earlyCheck =
		localDocumentStatus === 'processing' &&
		logsData?.document_check?.status === 'flagged'
			? logsData.document_check
			: null;
	// ROC-72: completeness/readability verdict from the latest run.
	const documentCheck =
		earlyCheck ??
		refreshedDocument?.results?.document_check ??
		document.results?.document_check;
	// A bounced file: the banner already says why, so skip the failure log and empty analysis.
	const bounced =
		documentCheck?.status === 'flagged' && localDocumentStatus === 'failed';
	const hideAnalysis = blocksQuote(documentCheck) || bounced;
	const bounceRepliesOn = useFeature('bounce_replies');
	// The API decides what costs a report; the pop-ups only show it.
	const rerunUsesReport = Boolean(refreshedDocument?.rerun_uses_report);
	const replaceUsesReport = Boolean(refreshedDocument?.replace_uses_report);

	// ROC-105: flagged figures lock Customize Quote until a person confirms them.
	const lineReview = lineReviewFrom(lineItemsData);
	// v9 st-hearth: a partly readable file gets its own banner, not the ROC-72 alert.
	const partlyReadable = isPartlyReadable(documentCheck);
	const [confirmLineItems, { isLoading: isConfirming }] =
		useConfirmLineItemsMutation();
	const handleConfirm = async (lineItemIds?: string[]) => {
		try {
			await confirmLineItems({ fileId: document.id, lineItemIds }).unwrap();
		} catch (error: any) {
			showToast(
				'Could not confirm',
				error?.data?.message || 'Please try again.',
				'error',
			);
		}
	};
	// ROC-102: roles that may not see ISO profit get no profit card or dock figure.
	const { canSeeIsoProfit } = useIsoProfitAccess();
	const visibleRevenue = canSeeIsoProfit ? revenueAnalysis : undefined;

	// "Keep my selection" hides the banner for this statement (session-only).
	const showTypeMismatchBanner =
		hasTypeMismatch && dismissedMismatchForId !== document.id;

	// ROC-116: a first run, or a file that did not reach the analysis, opens as the case page.
	const showStatementState =
		useFeature('statement_states') && (showProgress || hideAnalysis);

	// ROC-114: live slider for IC+ quotes with one discount rate. The dock shows
	// the client projection at once and saves the rate 500 ms after the last move.
	const slider = useMemo((): DiscountRateSlider | undefined => {
		const baseRate = quoteDiscountRate(customRates);
		const items = lineItemsData?.line_items ?? [];
		const totals = lineItemsData?.calculated_totals;
		if (
			baseRate === null ||
			lineItemsData?.rate_plan_type !== 'IC_PLUS' ||
			!totals ||
			discountLines(items).length === 0
		) {
			return undefined;
		}
		const base = {
			proposedTotal: totals.comparison_processor.grand_total,
			monthlySavings: totals.savings.monthly,
			volume: totals.comparison_processor.volume,
		};
		return {
			baseRate,
			project: (rate) => projectDiscountRate(items, viewMode, rate, base),
			onSave: async (rate) => {
				const rates = customRates.rates;
				try {
					await setDiscountRate({
						fileId: document.id,
						directRates: {
							rate_plan_type: customRates.rate_plan_type,
							selected_rate_types: customRates.selected_rate_types,
							pricing_mode: customRates.pricing_mode,
							pass_through_costs: customRates.pass_through_costs,
							monthly_and_batch_fees: customRates.monthly_and_batch_fees,
							rates: {
								...rates,
								tiered_rates: rates.tiered_rates?.mode
									? rates.tiered_rates
									: null,
								standard_rates: {
									...rates.standard_rates,
									same_for_all: {
										...rates.standard_rates.same_for_all,
										discount_rate: rate,
									},
								},
							},
						},
					}).unwrap();
				} catch (error) {
					const message = (error as { data?: { message?: string } })?.data
						?.message;
					showToast(
						'Rate not saved',
						message ?? 'Could not save the discount rate.',
						'error',
					);
					throw error;
				}
			},
		};
	}, [
		customRates,
		lineItemsData,
		viewMode,
		document.id,
		setDiscountRate,
		showToast,
	]);

	const backendTotals = lineItemsData?.calculated_totals;

	// ROC-105: every way into Customize Quote honours the flagged-figures lock.
	const openCustomizeQuote = () => {
		if (lineReview.blocked) {
			showToast(
				'Confirm the figures first',
				quoteLockedMessage(lineReview),
				'error',
			);
			return;
		}
		setIsCustomizeModalOpen(true);
	};

	// Same gate as Customize Quote: the modal needs the quote's rates, and
	// flagged figures (ROC-105) lock the slider until they are confirmed.
	const showDock =
		localDocumentStatus === 'completed' &&
		!hideAnalysis &&
		!lineReview.blocked &&
		Boolean(customRates) &&
		Boolean(analysisData && monthlySavingsData);

	const documentPanel = (
		<DocumentViewerPanel
			isVisible={isDocumentVisible}
			onClose={toggleDocumentVisibility}
			document={{
				...enhancedDocument,
				name: document.name,
				type: document.type,
				date: document.date,
				size: document.size,
			}}
		/>
	);
	const addPagesModal = (
		<AddPagesModal
			request={uploadRequest}
			usesReport={
				uploadRequest?.mode === 'replace' ? replaceUsesReport : rerunUsesReport
			}
			wholeStatementUsesReport={replaceUsesReport}
			isSubmitting={isAddingPages}
			onClose={() => setUploadRequest(null)}
			onSubmit={handleAddPages}
		/>
	);

	if (showStatementState) {
		return (
			<div className="min-h-screen bg-gray-50">
				<StatementStateView
					fileName={document.name}
					processing={localDocumentStatus === 'processing' && !earlyCheck}
					check={documentCheck}
					uploadedAt={refreshedDocument?.created_at}
					uploadedBy={document.uploaded_by}
					statementMonth={document.processingMonth}
					pdfUrl={enhancedDocument.pdfUrl}
					onBack={onBack}
					onViewStatement={toggleDocumentVisibility}
					onRefresh={() => {
						refetchLogs();
						refetch?.();
					}}
					onUpload={
						localDocumentStatus === 'processing' ? undefined : setUploadRequest
					}
				/>
				{documentPanel}
				{addPagesModal}
			</div>
		);
	}

	return (
		<div ref={containerRef} className="flex flex-col bg-gray-50 min-w-[1200]">
			{/* ROC-114: the header scrolls away so the fee breakdown gets the room */}
			<div>
				<AnalysisHeader
					hideAnalysis={hideAnalysis}
					quoteLockedReason={
						lineReview.blocked ? quoteLockedMessage(lineReview) : undefined
					}
					document={{
						id: document.id,
						name: document.name,
						date: document.date,
						status: localDocumentStatus,
					}}
					onBack={onBack}
					onToggleDocument={toggleDocumentVisibility}
					isDocumentVisible={isDocumentVisible}
					onboardingContext={onboardingContext}
					analysisData={analysisData}
					monthlySavingsData={monthlySavingsData}
					calculatedTotals={calculatedTotals}
					revenueAnalysis={revenueAnalysis}
					analysisType={analysisType}
					actionType={actionType}
					logsData={logsData}
					onCustomizeFeeStructure={handleCustomizeFeeStructure}
					isLoading={isLoading}
					isFetching={isFetching}
					onRefetchData={handlePostRecalculation}
					customRates={customRates}
					onRerun={
						localDocumentStatus !== 'processing'
							? handleOpenRerunModal
							: undefined
					}
					isRerunning={isRerunning}
					viewMode={viewMode}
					onViewModeChange={setViewMode}
					hasUserEdits={hasUserEdits}
					hasCustomizedQuote={hasCustomizedQuote}
					isCustomizeModalOpen={isCustomizeModalOpen}
					onCustomizeModalOpenChange={setIsCustomizeModalOpen}
					typeMismatch={
						showTypeMismatchBanner && detectedRatePlanType
							? {
									detectedRatePlanType,
									selectedRatePlanType: selectedRatePlanType ?? '',
									onRecalculateAsDetected: handleRecalculateAsDetected,
									onDismiss: () => setDismissedMismatchForId(document.id),
									isRecalculating: isRerunning,
								}
							: undefined
					}
				/>
			</div>

			<div
				className={`flex-1 bg-gray-50 relative z-2 ${showDock ? 'pb-28' : ''}`}
			>
				<div className="space-y-0">
					{documentCheck?.status === 'flagged' && !partlyReadable && (
						<div className="px-6 pt-4">
							<DocumentCheckBanner
								check={documentCheck}
								failed={localDocumentStatus === 'failed'}
								onUpload={
									localDocumentStatus === 'processing'
										? undefined
										: setUploadRequest
								}
								mailAction={
									bounceRepliesOn &&
									needsReply(documentCheck) &&
									localDocumentStatus !== 'processing' ? (
										<BounceReplyDraft
											fileId={document.id}
											check={documentCheck}
											primary={isNotAStatement(documentCheck)}
										/>
									) : undefined
								}
							/>
						</div>
					)}
					{!hideAnalysis && documentCheck && partlyReadable && (
						<PartlyReadableBanner review={lineReview} check={documentCheck} />
					)}
					{/* Step 1: Processing Logs */}
					{!bounced && (
						<ProcessingStep
							isCollapsed={isLogsCollapsed}
							onToggle={() => setIsLogsCollapsed(!isLogsCollapsed)}
							status={localDocumentStatus}
							logsData={logsData}
							onRetry={refetchLogs}
							onRerun={handleOpenRerunModal}
							isRerunning={isRerunning}
						/>
					)}

					{!hideAnalysis && !partlyReadable && (
						<PersonReviewBanner
							review={lineReview}
							onShowLog={() => setIsLogsCollapsed(false)}
							onConfirmAll={() => handleConfirm()}
							isConfirming={isConfirming}
						/>
					)}

					{/* Step 2: Fee Analysis (hidden when the file is not a statement) */}
					{!hideAnalysis && (
						<LineReviewProvider
							value={{
								review: lineReview,
								onConfirm: handleConfirm,
								isConfirming,
							}}
						>
							<AnalysisStep
								status={localDocumentStatus}
								showProgress={showProgress}
								isLoading={isLoading}
								isFetching={isFetching}
								analysisData={analysisData}
								document={document}
								monthlySavingsData={monthlySavingsData}
								calculatedTotals={calculatedTotals}
								logsData={logsData}
								enableEditing={enableEditing}
								onLineItemUpdated={handleLineItemUpdated}
								viewMode={viewMode}
								onViewModeChange={setViewMode}
							/>
						</LineReviewProvider>
					)}
					{localDocumentStatus === 'completed' && !hideAnalysis && (
						<AnalysisSideCards
							revenueAnalysis={visibleRevenue ?? undefined}
							analysisType={analysisType}
							checklist={[
								...checklistItems({
									printedTotal: backendTotals?.document_total?.amount,
									calculatedTotal: backendTotals?.calculated_total,
									templateName: customRates?.name,
									onChangeTemplate: lineReview.blocked
										? undefined
										: openCustomizeQuote,
								}),
								// ROC-105 figures check, in the v9 checklist.
								...(lineReview.blocked
									? [
											{
												state: 'warn' as const,
												text: quoteLockedMessage(lineReview),
											},
										]
									: lineReview.confirmed.size > 0 || lineReview.statementChecked
										? [
												{
													state: 'ok' as const,
													text: 'Flagged figures confirmed',
												},
											]
										: []),
							]}
						/>
					)}
				</div>
			</div>

			{showDock && analysisData && monthlySavingsData && (
				<DiscountRateDock
					customRates={customRates}
					proposedFees={calculatedTotals?.icPlus ?? analysisData.totals.icPlus}
					effectiveRate={calculatedTotals?.effectiveRates?.comparison ?? null}
					monthlySavings={monthlySavingsData.value}
					savingsIsNegative={monthlySavingsData.isNegative}
					onAdjust={openCustomizeQuote}
					isoProfit={visibleRevenue?.total_revenue}
					slider={slider}
				/>
			)}

			{/* Document Viewer Panel */}
			{documentPanel}

			<Modal
				isOpen={isRerunModalOpen}
				onClose={handleCloseRerunModal}
				size="md"
				title="Rerun Analysis"
			>
				<ModalBody>
					<p className="text-sm text-gray-600">
						We'll start the analysis over from the beginning (not using old
						info). You'll still see your current results while we work.{' '}
						{rerunUsesReport
							? 'This uses 1 report.'
							: "It won't cost extra credits."}{' '}
						Do you want to continue?
					</p>
				</ModalBody>
				<ModalFooter>
					<Button
						variant="outline"
						onClick={handleCloseRerunModal}
						disabled={isRerunning}
					>
						Cancel
					</Button>
					<Button
						variant="primary"
						onClick={handleConfirmRerun}
						loading={isRerunning}
					>
						Confirm Rerun
					</Button>
				</ModalFooter>
			</Modal>
			{addPagesModal}
		</div>
	);
};

export default AnalysisView;
